import React, { useState, useRef } from 'react';

/*
 * Primary conversion point.
 *
 * Deliberately four fields. Nobody knows the brand yet, so the job here is to
 * start a conversation, not to run intake — qualification happens in the reply.
 *
 * ── Wiring it up ───────────────────────────────────────────────────────────
 *
 * Set REACT_APP_FORM_ENDPOINT in .env to a form backend URL. The POST is a
 * plain JSON body with an Accept: application/json header, which is the common
 * shape across Formspree, Basin and Web3Forms — so the service can be swapped
 * without touching this file. See .env.example.
 *
 * With no endpoint configured the form still works: submitting opens the
 * visitor's mail client with the answers pre-filled. That means the CTA is
 * never dead, even before a backend exists.
 */

const FORM_ENDPOINT = process.env.REACT_APP_FORM_ENDPOINT || '';
/* Web3Forms requires its key as a form field; harmless for the others. */
const FORM_ACCESS_KEY = process.env.REACT_APP_FORM_ACCESS_KEY || '';
const CONTACT_EMAIL = 'hello@zenbul.com';

/*
 * Cooling topology, not an equipment checklist.
 *
 * How heat is rejected is the single highest-value thing to learn before a
 * conversation starts: it sets the tone band, the number of fans, the price,
 * and whether there is a product here at all. A dry-cooler site and a
 * cooling-tower site of identical capacity are completely different enquiries.
 *
 * Still one optional field — the form stays deliberately short, and everything
 * else (fan count, fan diameter, the ordinance metric) is qualification that
 * happens in the reply rather than intake we demand up front.
 */
const COOLING = [
  'Dry coolers',
  'Adiabatic dry coolers',
  'Air-cooled chillers',
  'Cooling towers',
  'Water-cooled / plant room',
  'Not sure yet',
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function Assessment() {
  const [values, setValues] = useState({ name: '', email: '', location: '', cooling: [] });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const honeypot = useRef(null);

  const set = (field) => (e) => {
    setValues((v) => ({ ...v, [field]: e.target.value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const toggleCooling = (item) =>
    setValues((v) => ({
      ...v,
      cooling: v.cooling.includes(item)
        ? v.cooling.filter((x) => x !== item)
        : [...v.cooling, item],
    }));

  function validate() {
    const next = {};
    if (!values.name.trim()) next.name = 'Please add your name.';
    if (!values.email.trim()) next.email = 'Please add a work email.';
    else if (!EMAIL_RE.test(values.email.trim())) next.email = 'That doesn’t look like an email address.';
    if (!values.location.trim()) next.location = 'Where is the site?';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function mailtoFallback() {
    const body = [
      `Name: ${values.name}`,
      `Email: ${values.email}`,
      `Site location: ${values.location}`,
      `Cooling plant: ${values.cooling.join(', ') || 'not specified'}`,
    ].join('\n');
    window.location.href =
      `mailto:${CONTACT_EMAIL}` +
      `?subject=${encodeURIComponent('Site assessment request')}` +
      `&body=${encodeURIComponent(body)}`;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (status === 'sending') return;

    // Bots fill every field they find; humans never see this one.
    if (honeypot.current && honeypot.current.value) return;

    if (!validate()) {
      // Move focus to the first problem so keyboard and screen-reader users
      // aren't left guessing why nothing happened.
      const first = ['name', 'email', 'location'].find((f) => !values[f].trim() || errors[f]);
      const el = first && document.getElementById(`assessment-${first}`);
      if (el) el.focus();
      return;
    }

    if (!FORM_ENDPOINT) {
      mailtoFallback();
      return;
    }

    setStatus('sending');
    try {
      const res = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          ...(FORM_ACCESS_KEY ? { access_key: FORM_ACCESS_KEY } : {}),
          _subject: `Site assessment request: ${values.name}`,
          name: values.name.trim(),
          email: values.email.trim(),
          site_location: values.location.trim(),
          cooling_plant: values.cooling.join(', '),
        }),
      });
      if (!res.ok) throw new Error(`Form endpoint returned ${res.status}`);
      setStatus('sent');
    } catch (err) {
      console.error('[assessment] submit failed:', err);
      setStatus('error');
    }
  }

  if (status === 'sent') {
    return (
      <section className="assessment" id="assessment">
        <div className="wrap assessment-inner">
          <div className="assessment-copy">
            <h2 className="h2 assessment-title">
              Engineering silence starts with one night on site.
            </h2>
          </div>
          <div className="assessment-form">
            <div className="assessment-done" role="status">
              <p className="assessment-done-title">Thanks, we’ve got it.</p>
              <p className="assessment-done-body">
                We’ll be in touch shortly to get the process started.
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="assessment" id="assessment">
      <div className="wrap assessment-inner">
        <div className="assessment-copy">
          <h2 className="h2 assessment-title">
            Engineering silence starts with one night on site.
          </h2>
          {/* The hedge in the last clause is deliberate and should survive
              edits: we characterise the plant and say whether a guarantee can
              be written on it. Promising the SLA itself, sight unseen, is a
              commitment nobody can make from a web form. */}
          <p className="assessment-lead">
            Tell us where the site is and how the heat gets rejected. The first
            step is a night on site at our cost. We characterise your plant,
            and tell you whether an SLA is writable on it.
          </p>
          <p className="assessment-alt">
            Prefer email?{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="assessment-mail">
              {CONTACT_EMAIL}
            </a>
          </p>
        </div>

        <div className="assessment-form">
          <form className="form" onSubmit={handleSubmit} noValidate>
            <div className="form-row">
              <div className="field">
                <label className="field-label" htmlFor="assessment-name">
                  Name
                </label>
                <input
                  id="assessment-name"
                  className={`field-input${errors.name ? ' is-error' : ''}`}
                  type="text"
                  name="name"
                  autoComplete="name"
                  value={values.name}
                  onChange={set('name')}
                  aria-invalid={Boolean(errors.name)}
                  aria-describedby={errors.name ? 'assessment-name-err' : undefined}
                />
                {errors.name && (
                  <p className="field-error" id="assessment-name-err">
                    {errors.name}
                  </p>
                )}
              </div>

              <div className="field">
                <label className="field-label" htmlFor="assessment-email">
                  Work email
                </label>
                <input
                  id="assessment-email"
                  className={`field-input${errors.email ? ' is-error' : ''}`}
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={values.email}
                  onChange={set('email')}
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? 'assessment-email-err' : undefined}
                />
                {errors.email && (
                  <p className="field-error" id="assessment-email-err">
                    {errors.email}
                  </p>
                )}
              </div>
            </div>

            <div className="field">
              <label className="field-label" htmlFor="assessment-location">
                Site location
              </label>
              <input
                id="assessment-location"
                className={`field-input${errors.location ? ' is-error' : ''}`}
                type="text"
                name="location"
                placeholder="City, county, state"
                value={values.location}
                onChange={set('location')}
                aria-invalid={Boolean(errors.location)}
                aria-describedby={errors.location ? 'assessment-location-err' : undefined}
              />
              {errors.location && (
                <p className="field-error" id="assessment-location-err">
                  {errors.location}
                </p>
              )}
            </div>

            <fieldset className="field fieldset">
              <legend className="field-label">
                How is heat rejected on site?{' '}
                <span className="field-optional">optional</span>
              </legend>
              <div className="chips">
                {COOLING.map((item) => {
                  const on = values.cooling.includes(item);
                  return (
                    <button
                      type="button"
                      key={item}
                      className={`chip${on ? ' is-on' : ''}`}
                      aria-pressed={on}
                      onClick={() => toggleCooling(item)}
                    >
                      {item}
                    </button>
                  );
                })}
              </div>
            </fieldset>

            {/* Honeypot — off-screen, not display:none, so bots still fill it. */}
            <input
              ref={honeypot}
              type="text"
              name="_gotcha"
              className="sr-only"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />

            <div className="form-actions">
              <button
                type="submit"
                className="btn btn--primary form-submit"
                disabled={status === 'sending'}
              >
                {status === 'sending' ? 'Sending…' : 'Request assessment'}
              </button>
              <p className="form-note">
                We reply within a week. We won&rsquo;t add you to a mailing list.
              </p>
            </div>

            {status === 'error' && (
              <p className="form-alert" role="alert">
                That didn’t go through. Email{' '}
                <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and we’ll
                pick it up from there.
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}

export default Assessment;
